import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { BarChart3, Calendar, Share2, Plane, TrendingUp, Ship, TramFront, Globe, Truck, Snowflake, Bot, Zap } from 'lucide-react';
import { useLanguage, LanguageSwitcher } from '@/components/LanguageContext';

const articles = [
  {
    id: 1,
    title: "Severe Winter Weather Continues to Disrupt Northern European Logistics",
    description: "Heavy snow and ice impact major ports and inland transport networks across Northern Europe.",
    image: "https://imgs.newseditor.aws.kuehne-nagel.cloud/6bd1b1b2-9b88-49f2-8b12-167579828f77.jpg",
    category: "Weather",
    icon: Snowflake,
    color: "rgb(59,130,246)",
    date: "January 8, 2026",
    stats: [
      { label: "Ports Affected", value: "Major", color: "text-blue-600" },
      { label: "Delay Duration", value: "24-48h", color: "text-orange-600" },
      { label: "Impact Level", value: "High", color: "text-red-600" }
    ]
  },
  {
    id: 2,
    title: "Pre-Chinese New Year Shipping Patterns Diverge Across Trade Routes",
    description: "Asia-Europe routes surge while transpacific volumes remain muted amid economic uncertainty.",
    image: "https://images.unsplash.com/photo-1628097045676-97212b505372?w=800&auto=format&fit=crop&q=80",
    category: "Shipping",
    icon: Ship,
    color: "rgb(59,130,246)",
    date: "January 8, 2026",
    stats: [
      { label: "Asia-Europe Capacity", value: "1.15M TEU", color: "text-blue-600" },
      { label: "Growth vs Pre-COVID", value: "+50%", color: "text-green-600" },
      { label: "Blank Sailings", value: "Minimal", color: "text-orange-600" }
    ]
  },
  {
    id: 3,
    title: "AI-Powered System Set to Transform Container Loss Detection at Sea",
    description: "Eyesea and EVI Safety Technologies launch revolutionary AI system for real-time container monitoring.",
    image: "https://imgs.newseditor.aws.kuehne-nagel.cloud/13467df1-bff7-4496-8cd1-f2e5cda42a4c.jpg",
    category: "Technology",
    icon: Bot,
    color: "rgb(34,197,94)",
    date: "January 8, 2026",
    stats: [
      { label: "Detection Method", value: "AI Vision", color: "text-blue-600" },
      { label: "Compliance", value: "IMO Ready", color: "text-green-600" },
      { label: "Trial Status", value: "Active", color: "text-orange-600" }
    ]
  },
  {
    id: 4,
    title: "Looking Ahead 2026: From Volatility to Velocity in Global Logistics",
    description: "Industry analysis reveals strategic shift toward adaptive operations and enhanced visibility.",
    image: "https://www.maersk.com/~/media_sc9/maersk/news/news/images/2025/11/14/maersk-smart-truck-at-logistics-hub_1024x576.jpg?w=1056&hash=6ECD22F5638AD4A691AF6FABEFFD5954",
    category: "Strategy",
    icon: TrendingUp,
    color: "rgb(168,85,247)",
    date: "January 8, 2026",
    stats: [
      { label: "Focus Shift", value: "Velocity", color: "text-blue-600" },
      { label: "Key Factor", value: "Adaptability", color: "text-green-600" },
      { label: "Priority", value: "Visibility", color: "text-orange-600" }
    ]
  },
  {
    id: 5,
    title: "South Korea Accelerates Arctic Shipping Push with September Trial Run",
    description: "3,000-TEU vessel to test Busan-Rotterdam route through Arctic waters in strategic expansion.",
    image: "https://images.unsplash.com/photo-1524351364533-7e6bbb2447a6?w=800&auto=format&fit=crop&q=80",
    category: "Infrastructure",
    icon: Globe,
    color: "rgb(34,197,94)",
    date: "January 8, 2026",
    stats: [
      { label: "Vessel Size", value: "3,000 TEU", color: "text-blue-600" },
      { label: "Route", value: "Arctic", color: "text-green-600" },
      { label: "Timeline", value: "Sept 2026", color: "text-orange-600" }
    ]
  },
  {
    id: 6,
    title: "Global Logistics Robots Market Reaches $43.5B by 2035",
    description: "Explosive growth driven by warehouse automation and e-commerce demand transformation.",
    image: "https://images.unsplash.com/photo-1583911300103-0aaeff286030?w=800&auto=format&fit=crop&q=80",
    category: "Automation",
    icon: Bot,
    color: "rgb(59,130,246)",
    date: "January 8, 2026",
    stats: [
      { label: "Market Size 2035", value: "$43.5B", color: "text-blue-600" },
      { label: "CAGR", value: "16.2%", color: "text-green-600" },
      { label: "Top Segment", value: "AGVs 36.6%", color: "text-orange-600" }
    ]
  },
  {
    id: 7,
    title: "Boston Dynamics & Google DeepMind Form AI Partnership for Humanoid Robots",
    description: "Strategic alliance combines athletic intelligence with Gemini Robotics foundation models.",
    image: "https://images.unsplash.com/photo-1696258686454-60082b2c33e2?w=800&auto=format&fit=crop&q=80",
    category: "AI Partnership",
    icon: Zap,
    color: "rgb(168,85,247)",
    date: "January 8, 2026",
    stats: [
      { label: "Focus", value: "Humanoids", color: "text-blue-600" },
      { label: "AI Model", value: "Gemini", color: "text-green-600" },
      { label: "Target", value: "Manufacturing", color: "text-orange-600" }
    ]
  },
  {
    id: 8,
    title: "Amazon Expands Robotics Innovation with Strategic Acquisitions",
    description: "Major investments in delivery and packaging automation drive next-generation logistics.",
    image: "https://images.unsplash.com/photo-1633174524827-db00a6b7bc74?w=800&auto=format&fit=crop&q=80",
    category: "E-commerce",
    icon: Truck,
    color: "rgb(59,130,246)",
    date: "January 8, 2026",
    stats: [
      { label: "Investment Focus", value: "Robotics", color: "text-blue-600" },
      { label: "Application", value: "Delivery", color: "text-green-600" },
      { label: "Innovation", value: "Packaging", color: "text-orange-600" }
    ]
  },
  {
    id: 9,
    title: "PepsiCo Leverages 3D Digital Twin Technology for Supply Chain Optimization",
    description: "Real-time digital twins and AI co-designers revolutionize facility planning and operations.",
    image: "https://images.unsplash.com/photo-1717386255773-a456c611dc4e?w=800&auto=format&fit=crop&q=80",
    category: "Digital Twin",
    icon: Zap,
    color: "rgb(34,197,94)",
    date: "January 8, 2026",
    stats: [
      { label: "Throughput Increase", value: "+20%", color: "text-blue-600" },
      { label: "CapEx Reduction", value: "10-15%", color: "text-green-600" },
      { label: "Issue Prevention", value: "90%", color: "text-orange-600" }
    ]
  }
];



const Index = () => {
  const { t } = useLanguage();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
      <div className="container mx-auto px-6 py-8 space-y-12">
        {/* Header Section */}
        <section id="industry-highlights">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center">
              <BarChart3 className="h-8 w-8 text-blue-600 mr-3" />
              <h2 className="text-3xl font-bold text-gray-900">{t('header.title')}</h2>
            </div>
            <LanguageSwitcher />
          </div>


          {/* Articles Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {articles.map((article) => {
              const IconComponent = article.icon;
              return (
                <Link key={article.id} to={`/article/${article.id}`}>
                  <Card className="hover:shadow-lg transition-shadow">
                    {/* Image Section */}
                    <div className="relative h-48 overflow-hidden rounded-t-lg">
                      <img 
                        src={article.image} 
                        alt={t(`article.${article.id}.title`)}
                        className="w-full h-[192px] object-cover"
                      />
                      <Badge 
                        className="absolute top-4 left-4 text-white border-transparent"
                        style={{ backgroundColor: article.color }}
                      >
                        <IconComponent className="h-3 w-3 mr-1" />
                        {t(`category.${article.category.toLowerCase().replace(/[^a-z]/g, '')}`)}
                      </Badge>
                    </div>

                    {/* Content Section */}
                    <CardHeader>
                      <CardTitle className="text-lg">{t(`article.${article.id}.title`)}</CardTitle>
                      <div className="flex items-center text-sm text-muted-foreground">
                        <Calendar className="h-4 w-4 mr-1" />
                        {article.date}
                      </div>
                    </CardHeader>

                    <CardContent>
                      <p className="text-gray-600 mb-4">{t(`article.${article.id}.description`)}</p>
                      
                      {/* Stats */}
                      <div className="space-y-2 mb-4">
                        {article.stats.map((stat, index) => (
                          <div key={index} className="flex justify-between">
                            <span className="text-sm text-gray-600">{stat.label}:</span>
                            <span className={`font-semibold ${stat.color}`}>{stat.value}</span>
                          </div>
                        ))}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex justify-between items-center">
                        <Badge variant="secondary">{t(`category.${article.category.toLowerCase().replace(/[^a-z]/g, '')}`)}</Badge>
                        <div className="flex space-x-2">
                          <Button variant="outline" size="sm">{t('common.readMore')}</Button>
                          <Button variant="outline" size="sm" className="ml-auto">
                            <Share2 className="h-4 w-4 mr-2" />
                            {t('common.share')}
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </section>

        {/* Footer */}
        <Separator />
        <footer>
          <div className="container mx-auto px-6">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div>
                <h3 className="text-xl font-bold mb-2">{t('footer.title')}</h3>
                <p className="text-blue-200">{t('footer.subtitle')}</p>
                <p className="text-blue-200 text-sm">{t('footer.lastUpdated')}</p>
              </div>
              <div className="mt-4 md:mt-0">
                {/* Additional footer content can go here */}
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default Index;